import type { EasterEgg } from './engine'
// A snippet of ICF Worship's "Go and Tell" (instrumental demo): the intro, from
// where the instruments come in up to the start of the first verse (~13s).
// Webpack (asset/resource) emits it and hands back its hashed public URL. See
// the 'konami' egg below; the light and confetti durations are matched to it
// and the audio fades out at the end.
import goAndTellClip from '../../../audio/go-and-tell-clip.mp3'

// Length of goAndTellClip in ms - the Konami lights and confetti are stretched
// to this so the whole "worship moment" ends together with the music. The last
// konamiFadeMs of the clip fade the audio down to silence instead of cutting.
const konamiClipMs = 13000
const konamiFadeMs = 1500

/**
 * Easter egg registry: pure data, no code. Adding an egg means adding an
 * entry here - the engine and effect modules stay untouched.
 *
 * Adding one also resets the completion reward for everyone, all by itself:
 * the golden logo glow needs every slug in this list, and the celebration is
 * remembered per egg-set (see effects/completion.ts). So earlier finds are kept
 * and the reward has to be re-earned by finding the newcomer.
 */
const eggs: EasterEgg[] = [
  {
    slug: 'heaven',
    terms: [
      'heaven', // en
      'himmel', // de
      'hemel', // nl
      'ciel', // fr
      'cielo', // es + it
      'céu', // pt-BR
      'nebe', // cs
      'nebesa', // cs biblical
      'niebo', // pl
      'небо', // ru + uk
      'небеса', // ru + uk biblical
      'qiell', // sq
      'qielli', // sq definite form
      'שמים', // he
      'שמיים', // he colloquial spelling
      'ឋានសួគ៌' // km
    ],
    sources: ['location-finder'],
    effects: [{ type: 'glow' }, { type: 'sound' }]
  },
  {
    slug: 'party',
    terms: [
      'party', // en/de/nl + widely understood
      'feiern', // de
      'párty', // cs
      'fiesta', // es
      'festa', // it + pt-BR
      'fête', // fr
      'impreza', // pl
      'festë', // sq
      'вечеринка', // ru
      'вечірка', // uk
      'מסיבה', // he
      'ជប់លៀង', // km
      'confetti', // en/nl (it: see coriandoli)
      'konfetti', // de + pl
      'confeti', // es
      'confettis', // fr
      'confete', // pt-BR
      'konfety', // cs
      'coriandoli', // it
      'конфетти', // ru
      'конфеті', // uk
      'קונפטי' // he
    ],
    sources: ['events-list'],
    when: 'always',
    effects: [{ type: 'confetti' }, { type: 'sound', synth: 'pop' }]
  },
  {
    slug: 'sheep',
    // 'contains': matches as soon as one of these appears as a word (or
    // word start) in the query - "der gute Hirte" or "Schafe" also count
    match: 'contains',
    terms: [
      'sheep', // en
      'shepherd', // en
      'lamb', // en
      'schaf', // de (word-start match also covers "Schafe")
      'schaaf', // de common typo (does not start with "schaf")
      'lamm', // de
      'hirte', // de (also matches "Hirten")
      'schaap', // nl
      'herder', // nl
      'mouton', // fr
      'brebis', // fr biblical
      'berger', // fr
      'oveja', // es
      'pecora', // it
      'ovelha', // pt-BR
      'ovce', // cs
      'pastýř', // cs
      'owca', // pl
      'pasterz', // pl
      'овца', // ru
      'пастух', // ru + uk
      'вівця', // uk
      'dele', // sq
      'כבש', // he
      'רועה', // he shepherd
      'ចៀម', // km
      'គង្វាល' // km shepherd
    ],
    sources: ['sermons-list'],
    when: 'always',
    effects: [{ type: 'sheep' }]
  },
  {
    // no terms: triggered manually from the 404 page
    // (blocks/yellowtree/not-found/view.ts) when the hidden sheep is found.
    // Deliberately without effects - finding it stays plain, the only reaction
    // is the sheep's own jump animation
    slug: 'lost-sheep',
    effects: []
  },
  {
    // no terms: triggered when the sheep run mini-game is won
    // (assets/frontend/js/easterEggs/games/sheepRun), started from the 404 page
    slug: 'sheep-home',
    effects: [{ type: 'confetti' }, { type: 'sound', synth: 'fanfare' }]
  },
  {
    // no terms: triggered once every sheep run level has been cleared - the
    // full badge set (see games/sheepRun, badgeName())
    slug: 'sheep-champion',
    effects: [{ type: 'confetti', particleCount: 320, durationMs: 5000 }],
    repeat: 'session'
  },
  {
    // no terms: triggered from the global Konami-code key sequence
    // (assets/frontend/js/easterEggs/globalTriggers.ts) - a full-screen
    // "worship moment": sweeping party/stage lights, a snippet of ICF Worship's
    // "Go and Tell" (goAndTellClip above) and a burst of confetti
    slug: 'konami',
    effects: [
      { type: 'lights', durationMs: konamiClipMs },
      { type: 'sound', src: goAndTellClip, volume: 0.5, fadeOutMs: konamiFadeMs },
      { type: 'confetti', durationMs: konamiClipMs }
    ]
  },
  {
    // no terms: triggered from the footer logo click combo - wobble and
    // messages live in blocks/yellowtree/footer/view.ts because the text
    // escalates per round
    slug: 'logo-clicks',
    effects: []
  },
  {
    // no terms: fired from globalTriggers.ts when the pointer draws an upright
    // cross (the inverted cross is deliberately ignored). The cross itself is
    // rendered at the drawn spot by the detector (playCross); here we add the
    // same "holy glow" the 'heaven' search egg uses.
    slug: 'draw-cross',
    effects: [{ type: 'glow' }, { type: 'sound' }]
  }
]

export default eggs
